/* eslint-disable max-len */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import { Document, OEMCompatibility } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/Reference';
import {
  Origin,
  ReferenceSource,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferenceTradingDataRequest';
import { History } from 'history';
import { Dispatch } from 'redux';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { ArrowsCompareIcon } from 'assets/icons';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { DiscountCorner } from 'components/Discount';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { positionHasPicture } from 'components/Filter/Filter.types';
import { OEMCompatibilityLine } from 'components/OEMCompatibilityLine';
import { PriceUnavailable, ReferencePriceSection } from 'components/ReferencePriceSection';
import { ReferenceUnavailableForOrder } from 'components/ReferenceUnavailableBox/ReferenceUnavailable';
import StockDisplay from 'components/StockInfo';
import { getIAMBrandCodeByVehicleBrand, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import {
  IAM,
  IAMLaborTimesNodeWrapper,
  OEM_BRAND_KEY,
  PARAM_LABOR_TIME,
  PreferredTabParam,
  STANDARD,
} from 'domains/catalog/Catalog.types';
import { convertReferenceToEstimateReference } from 'domains/estimate/Estimate.mapper';
import { addCatalogReference } from 'domains/estimate/Estimate.store';
import {
  formatReferenceLabel,
  getIAMReference,
  getPrices,
  getStockInfo,
  hasMultipleOffers,
  IAMReferenceLocal,
  ReferencePriceType,
} from 'domains/references';
import {
  getMatchingOEMCompatibility,
  isOtherBrandVehicle,
  isReferenceExadis,
  isRefMKTP,
} from 'domains/references/References.utils';
import { GarageView, getDealerType, getIamCatalogBrandsView, getUserRights, SparePartsViewType } from 'domains/user';
import ElectronicPartsRepairLink from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ElectronicPartsRepairLink';
import { IAMReferenceApplicability } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceApplicability/IAMReferenceApplicability';
import { PictureSection } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/PictureSection';
import { IAMReferenceMultiOffersModal } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceMultiOffersModal/IAMReferenceMultiOffersModal';
import {
  ASSETS,
  BlackButton,
  Box,
  CompareButton,
  Descriptions,
  Flex,
  FlexJustifyType,
  Icon,
  Image,
  INITIAL_ITEM_COUNT,
  Item,
  MarginBox,
  Spin,
  Text,
} from 'UI';
import { NotificationLink, notifyTop } from 'UI/Notification/notification';
import { BreakpointSelectable, getData, hasPrice, isTractionBatteryOrderBlocked, NO_DATA, textFormatter } from 'utils';
import { TRACKING_EVENT_ADD_TO_ESTIMATE, TRACKING_EVENT_GO_TO_ESTIMATE } from 'utils/eventTracker/EventTracker.types';
import { SCard } from './IAMReferenceCard.styled';

export const STANDARD_EXCHANGE_REFERENCE_PATTERN = '81008';
const FLEX_START: BreakpointSelectable<FlexJustifyType> = 'flex-start';

export const addToEstimateNotification = (handleNotificationOnClick?: () => void, quantity?: number) =>
  notifyTop(
    'success',
    <Trans count={quantity ?? 1} i18nKey={'catalog.reference_card.added_to_estimate.description.v2'}>
      {'{{ count }} Reference has been added to your estimate.'}
    </Trans>,
    undefined,
    <NotificationLink onClick={handleNotificationOnClick}>
      <Trans i18nKey={'catalog.reference_card.added_to_estimate.go_to_estimate'}>{'Go to estimate'}</Trans>
    </NotificationLink>,
  );

const renderDisplayName = (names: (string | undefined)[] = [], isPrimary = false, hideInitialDelimiter = false) =>
  names.filter(Boolean).map((label, index) => {
    return (
      <Text
        key={`iamRefDispName_${isPrimary ? 'prim' : 'sec'}_${index}`}
        type="base"
        bold={isPrimary}
        noWrap
        data-cy={'iam-product-designation'}
      >
        {!index && hideInitialDelimiter ? label : `/ ${label}`}&nbsp;
      </Text>
    );
  });

const LaborTimeLoading = () => <Spin />;

const LaborTimeNotFound = () => {
  const { t } = useTranslation();
  return (
    <Text type="sm" displayStyle={'disabled'} cursor={'pointer'} hoverUnderLine data-cy={'add-labor-time-button'}>
      {t('common.action.add_labor_time', 'Add Labor time')}
    </Text>
  );
};

const LaborTimeError = () => {
  const { t } = useTranslation();
  return (
    <ErrorWithLabel
      label={t('common.labor_times.backend_error.short', 'Labor times temporarily unavailable.')}
      narrow
    />
  );
};

interface AddToEstimateReference {
  referenceNumber: string;
  displayName: string;
  price?: ReferencePriceType;
  brand?: ReferenceBrandType;
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  quantity?: number;
  sellerNumber?: string;
  sellerName?: string;
}

export const handleAddToEstimate = (dispatch: Dispatch, history: History, reference: AddToEstimateReference) => {
  if (reference.price?.garageView?.vatExcludedPrice) {
    addToEstimateNotification(() => {
      trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);
      history.push(`${ROUTER_ESTIMATE}`);
    }, reference.quantity);
    trackAppEvent(TRACKING_EVENT_ADD_TO_ESTIMATE);
    dispatch(
      addCatalogReference({
        reference: convertReferenceToEstimateReference(
          reference.referenceNumber,
          reference.displayName,
          reference.price,
          true,
          'IAM',
          reference.brand,
          reference.origin,
          reference.supplierCode,
          reference.referenceSource,
          undefined,
          undefined,
          undefined,
          undefined,
          reference.quantity,
          reference.sellerNumber,
          reference.sellerName,
        ),
      }),
    );
  }
};

interface ReferenceCardProps {
  name?: string;
  supplier?: string;
  rawLabels: string[];
  secondaryNames?: string[];
  referenceNumber: string;
  brand?: ReferenceBrandType;
  price?: ReferencePriceType;
  sparePartsView: SparePartsViewType;
  vehicleKey: string | undefined;
  brandImage?: string;
  referenceImage?: string;
  laborTimesWrapper?: NO_DATA | IAMLaborTimesNodeWrapper;
  position: string | undefined;
  documents: Document[];
  isIamSearchResult?: boolean;
  selected?: boolean;
  isMaintenance?: boolean;
  onSelect?: (referenceNumber: string) => void;
  replacingReferences: string[];
  substitutedReferences: string[];
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  additionalInformationItems: Item[];
  handleAddToCart: (sellerName?: string, sellerNumber?: string) => void;
  handleClickCompare?: (reference: string) => void;
  linkedReferences?: string[];
  bordered?: boolean;
  oemCompatibilities?: OEMCompatibility[];
  sellerNumberIn?: string;
  sellerNameIn?: string;
  /** Per-reference traction battery vehicle compatibility (isBatteryCompatibleWithVehicle from IAM catalog API).
   * undefined = compatibility unknown (treated as compatible; no block).
   * false     = vehicle is incompatible → triggers VIN_NON_COMPLIANT block for R1/R2/COMMAND/CONNECT_COMMANDE. */
  isCompatible?: boolean;
}

const IAMReferenceCard = React.memo(function IAMReferenceCard({
  referenceNumber,
  name,
  supplier,
  rawLabels,
  secondaryNames,
  sparePartsView,
  brand,
  price,
  vehicleKey,
  brandImage,
  referenceImage,
  laborTimesWrapper,
  position,
  documents,
  isIamSearchResult,
  isMaintenance,
  selected,
  onSelect,
  replacingReferences,
  substitutedReferences,
  origin,
  supplierCode,
  referenceSource,
  additionalInformationItems,
  handleAddToCart,
  handleClickCompare,
  linkedReferences,
  bordered = true,
  oemCompatibilities,
  sellerNumberIn,
  sellerNameIn,
  isCompatible,
}: ReferenceCardProps) {
  const pricesMap = useSelector((state: RootState) => getPrices(state, referenceNumber));
  const sellers = [...(getData(pricesMap)?.values() ?? [])].map((price1) => ({
    sellerName: price1.sellerName,
    sellerNumber: price1.sellerNumber,
  }));
  // fill seller info if only one seller is available
  const sellerNumber = sellerNumberIn ?? sellers.length === 1 ? sellers[0]?.sellerNumber : undefined;
  const sellerName = sellerNameIn ?? sellers.length === 1 ? sellers[0]?.sellerName : undefined;
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const vehicleImage = vehicleDetail?.imageUrl;
  const detailWithVehicle = useSelector((state: RootState) =>
    getIAMReference(state, { vehicleKey: vehicleDetail?.vehicleKey, referenceNumber }),
  );
  const detailWithoutVehicle = useSelector((state: RootState) =>
    getIAMReference(state, { vehicleKey: undefined, referenceNumber }),
  );

  const stock = useSelector((state: RootState) => getStockInfo(state, referenceNumber));
  const stockData = getData(stock?.data);
  const detail = detailWithVehicle ?? detailWithoutVehicle;
  const detailData = getData(detail);
  const userRoles = useSelector(getUserRights);
  const resolvedUserRoles = Array.isArray(userRoles) ? userRoles : undefined;
  const iamCatalogBrandsView = useSelector(getIamCatalogBrandsView);
  const [showMore, setShowMore] = useState(false);
  const isR1 = useSelector(getDealerType) === 'R1';
  const availableForOrder = vehicleDetail?.vehicleBrand === 'ALPINE' ? isR1 : true;
  const commercialFamily = detailData?.commercialFamily;
  const orderBlocked = isTractionBatteryOrderBlocked(
    resolvedUserRoles,
    commercialFamily,
    vehicleDetail?.vin,
    isCompatible,
  );
  const canOrder = availableForOrder && !orderBlocked;
  const isOEM =
    (iamCatalogBrandsView === OEM_BRAND_KEY && !isIamSearchResult) || (origin === 'OE' && isIamSearchResult);
  const displayShowMoreButton = additionalInformationItems.length > INITIAL_ITEM_COUNT;
  const displaySubstitutionButton =
    displayShowMoreButton && (replacingReferences.length > 0 || substitutedReferences.length > 0);
  const initialItems = additionalInformationItems.slice(0, INITIAL_ITEM_COUNT);
  const [currentData, setCurrentData] = useState(initialItems);
  const displayName = [name, supplier, ...rawLabels].filter(Boolean).join(' / ');
  const translatedName = referenceNumber.startsWith(STANDARD_EXCHANGE_REFERENCE_PATTERN)
    ? `${name} [${t('catalog.iam.standard_exchange', 'STANDARD EXCHANGE')}]`
    : name;
  const primaryDisplayName = renderDisplayName([translatedName, supplier, ...rawLabels], true, true);
  const secondaryDisplayName = renderDisplayName(secondaryNames, false, !primaryDisplayName.length);
  const referenceWithMultipleOffers = useSelector((state: RootState) => hasMultipleOffers(state, referenceNumber));

  const vehicleBrandCode = useSelector((state: RootState) =>
    getIAMBrandCodeByVehicleBrand(state, vehicleDetail?.vehicleBrand),
  );

  const matchingOEM = React.useMemo(() => {
    if (!isOtherBrandVehicle(vehicleDetail?.vehicleBrand)) return undefined;
    return getMatchingOEMCompatibility(oemCompatibilities, vehicleBrandCode, vehicleDetail?.vehicleBrand);
  }, [oemCompatibilities, vehicleDetail?.vehicleBrand, vehicleBrandCode]);

  const handleShowMore = () => {
    setCurrentData(showMore ? initialItems : [...additionalInformationItems]);
    setShowMore((prevState) => !prevState);
  };

  const redirectToLaborTime = () => {
    const params = new URLSearchParams(location.search);
    params.set(PreferredTabParam, PARAM_LABOR_TIME);
    history.push(`${location.pathname}?${params}`);
  };

  const renderAddLaborTime = () => {
    return (
      <DataContainer
        data={laborTimesWrapper}
        Loading={LaborTimeLoading}
        NotFound={LaborTimeNotFound}
        ErrorState={LaborTimeError}
      >
        <Text hoverUnderLine onClick={() => redirectToLaborTime()} type="sm" displayStyle={'link'} cursor={'pointer'}>
          {t('common.action.add_labor_time', 'Add Labor time')}
        </Text>
        <Box width={10} />
      </DataContainer>
    );
  };

  const handleAddToEstimateClick = (quantity?: number, sellerNumberHandler?: string, sellerNameHandler?: string) => {
    handleAddToEstimate(dispatch, history, {
      referenceNumber,
      displayName,
      price,
      brand,
      origin,
      supplierCode,
      referenceSource,
      quantity,
      sellerNumber: sellerNumber ?? sellerNumberHandler,
      sellerName: sellerName ?? sellerNameHandler,
    });
  };
  const [isMultipleOffersModalOpen, setIsMultipleOffersModalOpen] = useState<boolean>(false);

  const renderPriceSection = (
    hideButtons = false,
    sellerNumber1?: string,
    sellerName1?: string,
    useCompactView = false,
  ) => {
    if (isOEM) {
      if (!hasPrice(sparePartsView, price)) {
        return <PriceUnavailable />;
      }
      return (
        <MarginBox mr={30}>
          <Flex direction={'column'} align={'center'} justify={'center'} minHeight={110}>
            <MarginBox mt={15}>
              <Text type={'h5_bold'}>
                {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
                  vat_exclude_price: textFormatter.formatCurrency(
                    Number(price?.garageView?.vatExcludedPrice),
                    price?.currency ?? '',
                  ),
                })}
              </Text>
            </MarginBox>
            {!hideButtons && !referenceWithMultipleOffers && (
              <Flex direction={'column-reverse'}>
                <BlackButton onClick={() => handleAddToEstimateClick()}>
                  {t('catalog.action.add_to_estimate', 'Add to estimate')}
                </BlackButton>
              </Flex>
            )}
          </Flex>
        </MarginBox>
      );
    }

    const getIsMktp = (detailVehicle: NO_DATA | IAMReferenceLocal, detailNoVehicle: NO_DATA | IAMReferenceLocal) => {
      const detail = detailVehicle ? detailVehicle : detailNoVehicle;
      return isRefMKTP(getData(detail));
    };

    return (
      <ReferencePriceSection
        align={'left'}
        referenceNumber={referenceNumber}
        vehicleKey={vehicleKey}
        sparePartsView={sparePartsView}
        handleAddToCartClick={handleAddToCart}
        isApplicableToCurrentVehicle={!isIamSearchResult}
        catalogSource={IAM}
        referenceType={STANDARD}
        availableForOrder={canOrder}
        origin={origin}
        referenceSource={referenceSource}
        supplierCode={supplierCode}
        isMKTP={getIsMktp(detailWithVehicle, detailWithoutVehicle)}
        isExadis={isReferenceExadis(IAM, brand, origin)}
        linkedReferences={linkedReferences}
        handleMultipleOffersButtonClick={setIsMultipleOffersModalOpen}
        hideButtons={
          hideButtons ||
          (getIsMktp(detailWithVehicle, detailWithoutVehicle) &&
            !stockData?.warehouses?.some((wh) => wh.confirmedQuantity > 0))
        }
        sellerNumber={sellerNumber1}
        sellerName={sellerName1}
        narrow={useCompactView}
        useCompactView={useCompactView}
      />
    );
  };

  const renderEstimateSection = (size: 'sm' | 'lg', sellerNumberHandler?: string, sellerNameHandler?: string) =>
    hasPrice(sparePartsView, price) && !isOEM && !isMaintenance ? (
      <AddToEstimateButtonAndDialog
        handleAddToEstimateClick={handleAddToEstimateClick}
        displayed={canOrder}
        isVehicleCatalog
        size={size}
        sellerNameHandler={sellerName ?? sellerNameHandler}
        sellerNumberHandler={sellerNumber ?? sellerNumberHandler}
      />
    ) : null;

  function handleClick(referenceNumber: string) {
    isMaintenance && onSelect && onSelect(referenceNumber);
  }

  function handlePreventPropagate(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  return (
    <>
      <SCard selected={selected} isSquared={!canOrder} onClick={() => handleClick(referenceNumber)} bordered={bordered}>
        {sparePartsView === GarageView && <DiscountCorner reference={referenceNumber} type={'catalog'} />}
        <Flex direction={'column'} data-cy={`iam-reference-card-${referenceNumber}`}>
          <MarginBox my={15} ml={5} mr={15}>
            <Flex wrap={'wrap'}>
              <MarginBox mr={5} />
              <PictureSection
                isOEM={isOEM}
                vehicleImage={vehicleImage}
                displaySubstitutionButton={displaySubstitutionButton}
                handleShowMore={handleShowMore}
                documents={documents}
                brandImage={brandImage}
                referenceImage={referenceImage}
              />
              <MarginBox mr={15} />
              <Flex direction={'column'} align={FLEX_START}>
                <Flex size={0} align={'center'}>
                  <CopySelectionWrapper referenceNumber={referenceNumber}>
                    <Text type="heading3" disableGutter data-cy={'reference-number'}>
                      {formatReferenceLabel(
                        t('catalog.reference_card.reference_number', 'Ref:'),
                        referenceNumber.toUpperCase(),
                      )}
                    </Text>
                  </CopySelectionWrapper>
                  <MarginBox ml={10} />
                  <CopyReferenceWithFeedback referenceNumber={referenceNumber} />
                  {!!detailData && (
                    <>
                      <MarginBox ml={5} />
                      <IAMReferenceApplicability
                        reference={detailData}
                        primaryDisplayName={primaryDisplayName}
                        secondaryDisplayName={secondaryDisplayName}
                        renderPriceSection={() => renderPriceSection(true, sellerNumber, sellerName)}
                      />
                    </>
                  )}
                  {!isOEM && (
                    <>
                      <MarginBox mr={1} />
                      <DocumentationAlertButton
                        reference={detailData}
                        vehicleDetail={vehicleDetail}
                        vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
                      />
                    </>
                  )}
                  {positionHasPicture(position, t) && (
                    <Flex minWidth={80}>
                      <MarginBox ml={15} />
                      <Image
                        src={`referencePositionInVehicle/${position}.png`}
                        type={ASSETS}
                        alt={'ref-vehicle-position'}
                        height={33}
                      />
                      <MarginBox ml={15} />
                    </Flex>
                  )}
                </Flex>
                <Flex wrap={'wrap'}>
                  {primaryDisplayName}
                  {secondaryDisplayName}
                </Flex>
                {!isOEM && (
                  <Flex direction={'column'} justify={FLEX_START}>
                    <StockDisplay
                      vehicleKey={vehicleKey}
                      referenceNumber={referenceNumber}
                      isApplicableToCurrentVehicle
                      handleMultipleOffersButtonClick={setIsMultipleOffersModalOpen}
                      sellerNumber={sellerNumber}
                    />
                  </Flex>
                )}
                <Flex direction={'column'} justify={FLEX_START}>
                  {laborTimesWrapper && <>{renderAddLaborTime()}</>}
                  <ElectronicPartsRepairLink referenceNumber={referenceNumber} type={'light_12'} />
                </Flex>
                {matchingOEM && <OEMCompatibilityLine oemCompatibility={matchingOEM} />}
              </Flex>
              <MarginBox mr={15} />
              <Flex direction={'row'} justify={'flex-end'} onClick={handlePreventPropagate} size={1}>
                {!isIamSearchResult && (
                  <Flex direction={'column'} size={0} justify={'flex-end'} align={'flex-end'}>
                    <MarginBox mr={15}>
                      <CompareButton
                        size={'middle'}
                        onClick={() => {
                          handleClickCompare?.(referenceNumber);
                        }}
                        stretch
                        shape={'round'}
                        outlined
                        data-cy={'compare-button'}
                      >
                        <Flex align={'center'} justify={'center'} gap={10}>
                          <Icon IconComponent={ArrowsCompareIcon} size={16} />
                          {t('common.action.compare', 'Compare')}
                        </Flex>
                      </CompareButton>
                    </MarginBox>
                  </Flex>
                )}
                {renderPriceSection(false, sellerNumber, sellerName)}
                {!referenceWithMultipleOffers && renderEstimateSection('sm')}
              </Flex>
            </Flex>
          </MarginBox>
          {additionalInformationItems.length > 0 && (
            <Descriptions
              title={t('catalog.details.additional_information', 'Additional information')}
              items={currentData}
              displayShowMoreButton={displayShowMoreButton}
              showMore={showMore}
              handleShowMore={handleShowMore}
            />
          )}
        </Flex>
      </SCard>
      <ReferenceUnavailableForOrder enabled={!availableForOrder && !orderBlocked} />
      {!!detailData && (
        <IAMReferenceMultiOffersModal
          reference={detailData}
          primaryDisplayName={primaryDisplayName}
          secondaryDisplayName={secondaryDisplayName}
          renderPriceSection={renderPriceSection}
          isOEM={isOEM}
          isOpen={isMultipleOffersModalOpen}
          setIsOpen={setIsMultipleOffersModalOpen}
          renderEstimateSection={renderEstimateSection}
        />
      )}
    </>
  );
});

export default IAMReferenceCard;
